import { useEffect, useState } from "react";
import { Card, Divider, Form, Steps, Typography } from "antd";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "Components/Navbar/Navbar";
import Footer from "Components/Footer/Footer";
import {
  sendSignupOtp,
  registerPortal,
  normalizePhoneNumber,
} from "./signup.service";
import {
  renderErrorNotifications,
  renderSuccessNotifications,
} from "helpers/error.helpers";
import { trackEvent, ANALYTICS_EVENTS } from "helpers/analytics.helpers";
import useCommunityMasterData from "hooks/useCommunityMasterData/useCommunityMasterData";

import Step1Identity from "./_blocks/Step1Identity";
import Step2Community from "./_blocks/Step2Community";
import Step3AccessModel from "./_blocks/Step3AccessModel";
import SignupRail from "./_blocks/SignupRail";

const { Title, Text } = Typography;

const STEPS = [
  { title: "Identity", description: "Your basic info" },
  { title: "About", description: "About your business" },
  { title: "Access Model", description: "Membership setup" },
];


function Signup() {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const {
    activityOptions,
    cityOptions,
    getAreaOptions,
    getCityName,
    getAreaDisplayName,
    loading: loadingMasterData,
    loadingGeo,
  } = useCommunityMasterData();

  const [searchParams] = useSearchParams();
  const membersParam = Number.parseInt(searchParams.get("members"), 10);
  const rateParam = Number.parseInt(searchParams.get("rate"), 10);

  const [currentStep, setCurrentStep] = useState(0);
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.SIGNUP_STARTED);
  }, []);

  const handleStep0Next = async () => {
    try {
      await form.validateFields(["name", "email", "phone", "gst_number"]);
      trackEvent(ANALYTICS_EVENTS.SIGNUP_STEP_COMPLETED, { step: 1 });
      setCurrentStep(1);
    } catch (err) {
      if (!err?.errorFields) throw err;
    }
  };

  const handleStep1Next = async () => {
    try {
      await form.validateFields([
        "role",
        "community_name",
        "city_code",
        "area_codes",
      ]);
      trackEvent(ANALYTICS_EVENTS.SIGNUP_STEP_COMPLETED, { step: 2 });
      setCurrentStep(2);
    } catch (err) {
      if (!err?.errorFields) throw err;
    }
  };

  const handleSendOtp = async () => {
    try {
      setSendingOtp(true);
      const { errors } = await sendSignupOtp(form.getFieldValue("phone"));
      if (errors?.length) {
        renderErrorNotifications(errors);
        return;
      }
      setOtpSent(true);
      renderSuccessNotifications({
        title: "OTP sent",
        message: "Check your phone for the code.",
      });
    } catch (err) {
      renderErrorNotifications([
        { message: err?.message || "Failed to send OTP." },
      ]);
    } finally {
      setSendingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setSendingOtp(true);
      const { errors } = await sendSignupOtp(form.getFieldValue("phone"));
      if (errors?.length) {
        renderErrorNotifications(errors);
        return;
      }
      renderSuccessNotifications({
        title: "OTP resent",
        message: "A new code was sent to your phone.",
      });
    } catch (err) {
      renderErrorNotifications([
        { message: err?.message || "Failed to resend OTP." },
      ]);
    } finally {
      setSendingOtp(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields(["has_membership", "otp"]);
      const {
        name,
        email,
        phone,
        role,
        community_name,
        city_code,
        area_codes,
        categories,
        has_membership,
        membership_name,
        membership_type,
        member_benefits,
        membership_required,
        monthly_price,
        otp,
        gst_number,
        website,
        instagram_url,
      } = form.getFieldsValue(true);
      const initial_application = {
        name: name.trim(),
        phone_number: normalizePhoneNumber(phone),
        email_id: email.trim(),
        account_types: [role],
        community_name: community_name?.trim(),
        website: website?.trim(),
        instagram_url: instagram_url || [],
        categories: categories || [],
        city_code,
        area_codes: area_codes || [],
        city: getCityName(city_code),
        areas: (area_codes || []).map(
          (code) => getAreaDisplayName(code) || code
        ),
        has_membership,
        ...(gst_number?.trim()
          ? {
              business_documents: [
                { type: "GST", value: gst_number.trim().toUpperCase() },
              ],
            }
          : {}),
        ...(has_membership === "yes"
          ? {
              membership_name: membership_name?.trim(),
              membership_type,
              member_benefits: member_benefits?.trim(),
              membership_required,
              ...(membership_type === "paid"
                ? { monthly_price }
                : {}),
            }
          : {}),
      };

      setSubmitting(true);
      const { data: regData, errors: regErrors } = await registerPortal({
        name,
        email_id: email.trim(),
        phone_number: normalizePhoneNumber(phone),
        otp: otp.trim(),
        account_types: [role],
        initial_application,
      });

      if (regErrors?.length) {
        renderErrorNotifications(regErrors);
        return;
      }

      trackEvent(ANALYTICS_EVENTS.SIGNUP_SUBMITTED, { role });

      renderSuccessNotifications({
        title: "Registration successful",
        message: regData?.message || "Welcome to ShoUp!",
      });

      const applyPath = regData?.account_id
        ? `/application-status?account_id=${regData.account_id}`
        : "/application-status";
      navigate(applyPath);
    } catch (err) {
      if (!err?.errorFields)
        renderErrorNotifications([
          { message: err?.message || "Registration failed. Please try again." },
        ]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1 bg-gradient-shoup px-4 py-12 md:py-16">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-10 max-w-[22ch]">
            <Title level={2} style={{ margin: 0 }}>
              Register your community
            </Title>
            <Text type="secondary">
              Three steps. We&apos;ll take it from there.
            </Text>
          </div>

          <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-14">
            <Card
              style={{ borderRadius: 20, alignSelf: "start" }}
              styles={{ body: { padding: "32px 36px" } }}
            >
              <Steps
                current={currentStep}
                items={STEPS}
                size="small"
                style={{ marginBottom: 36 }}
              />

              <Form form={form} layout="vertical">
                {currentStep === 0 && <Step1Identity onNext={handleStep0Next} />}
                {currentStep === 1 && (
                  <Step2Community
                    form={form}
                    onNext={handleStep1Next}
                    onBack={() => setCurrentStep(0)}
                    activityOptions={activityOptions}
                    loadingActivities={loadingMasterData}
                    cityOptions={cityOptions}
                    getAreaOptions={getAreaOptions}
                    loadingGeo={loadingGeo}
                  />
                )}
                {currentStep === 2 && (
                  <Step3AccessModel
                    onBack={() => setCurrentStep(1)}
                    onSubmit={handleSubmit}
                    submitting={submitting}
                    otpSent={otpSent}
                    sendingOtp={sendingOtp}
                    onSendOtp={handleSendOtp}
                    onResendOtp={handleResendOtp}
                  />
                )}
              </Form>

              <Divider style={{ margin: "24px 0 16px" }} />
              <Text type="secondary" style={{ fontSize: 13 }}>
                Already have an account?{" "}
                <Link to="/login" style={{ fontWeight: 600 }}>
                  Sign in
                </Link>
              </Text>
            </Card>

            <SignupRail
              members={Number.isFinite(membersParam) ? membersParam : undefined}
              bookingRate={Number.isFinite(rateParam) ? rateParam : undefined}
            />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default Signup;
